/**
 * Shared email validation for the frontend.
 *
 * Replaces 8+ inline copies of `/^[^\s@]+@[^\s@]+\.[^\s@]+$/` that lived in
 * components like AddRFIModal, SiteVisitEmailModal, PillInput, etc.
 *
 * Behavior is intentionally identical to the previous inline regex — this is
 * an *extract*, not a *fix*. Known limitations are documented inline and on
 * the test suite. Do not tighten without a separate, deliberate change.
 *
 * Behavior contract (preserved from the inline regex):
 *  - Requires exactly one `@`, at least one non-whitespace/non-@ char on each
 *    side, and at least one `.` in the domain followed by more characters.
 *  - Trims leading/trailing whitespace before testing (matches the trim+test
 *    idiom every prior call site already used).
 *  - Allows characters the strict RFC would reject (e.g. unicode, IP-literal
 *    domains in brackets, missing length cap). Documented in the test suite.
 *  - Does NOT accept display-name format ("Name <a@b.com>"). That is a
 *    parse-then-validate problem, not a pure validate.
 *
 * For known limitations callers want to revisit later, file an issue and
 * mark the regex with a TODO referencing it; do not patch in-flight without
 * the corresponding test updates.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Returns true if `value` looks like a valid email address by the same loose
 * heuristic the legacy inline regex used. The input is trimmed before testing.
 *
 * Non-string / null / undefined inputs return false rather than throwing,
 * which matches what the inline `RE.test(maybeUndefined)` calls effectively
 * did once TypeScript narrowed them.
 */
export function isValidEmail(value: string | null | undefined): boolean {
  if (typeof value !== 'string') return false;
  return EMAIL_RE.test(value.trim());
}
